import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PriorityHighRounded as WarningIcon,
} from '@mui/icons-material';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

export interface NamedDeleteButtonProps {
  displayField: string;
}

const getByPath = (obj: unknown, path: string): string => {
  const segments = path.split('.');
  let current: unknown = obj;
  for (const seg of segments) {
    if (current && typeof current === 'object' && seg in current) {
      current = (current as Record<string, unknown>)[seg];
    } else {
      return '';
    }
  }
  return current == null ? '' : String(current);
};

export const NamedDeleteButton: React.FC<NamedDeleteButtonProps> = ({
  displayField,
}) => {
  const record = useRecordContext<{ id: string | number }>();
  const resource = useResourceContext();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [deleteOne, { isLoading }] = useDelete();
  const [open, setOpen] = useState(false);

  if (!record || !resource) return null;

  const handleConfirm = (): void => {
    deleteOne(
      resource,
      { id: record.id, previousData: record },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          const name = getByPath(record, displayField);
          notify(`resources.${resource}.notifications.deleted`, {
            type: 'success',
            messageArgs: { name, smart_count: 1 },
          });
          setOpen(false);
          setTimeout(() => {
            navigate(`/${resource}`, { replace: true });
            refresh();
          }, 0);
        },
        onError: (err: unknown) => {
          notify(extractErrorMessage(err, 'ra.notification.http_error', translate), {
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="error"
        startIcon={<DeleteIcon fontSize="small" />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{ textTransform: 'none', fontWeight: 500, minWidth: 0 }}
      >
        {translate('ra.action.delete')}
      </Button>

      <Dialog
        open={open}
        onClick={(e) => e.stopPropagation()}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          setOpen(false);
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: '90vw',
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, pb: 1 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: '4px solid',
              borderColor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <WarningIcon
              sx={{ fontSize: 40, color: 'warning.main', fontWeight: 700 }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}
          >
            {translate('common.deleteConfirmTitle')}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center' }}
          >
            {translate('common.deleteConfirmSubtitle')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
            disabled={isLoading}
            variant="contained"
            sx={(theme) => ({
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              height: 40,
              backgroundColor: `${theme.palette.error.main} !important`,
              color: `${theme.palette.common.white} !important`,
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: `${theme.palette.error.dark} !important`,
                boxShadow: 'none !important',
              },
              '&.Mui-disabled': {
                backgroundColor: `${theme.palette.error.light} !important`,
                color: `${theme.palette.common.white} !important`,
                opacity: 0.6,
              },
            })}
          >
            {translate('ra.action.delete')}
          </Button>
          <Button
            onClick={() => setOpen(false)}
            disabled={isLoading}
            variant="contained"
            sx={(theme) => ({
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              height: 40,
              backgroundColor: `${theme.palette.grey[200]} !important`,
              color: `${theme.palette.text.primary} !important`,
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: `${theme.palette.grey[300]} !important`,
                boxShadow: 'none !important',
              },
              '&.Mui-disabled': {
                backgroundColor: `${theme.palette.grey[100]} !important`,
                color: `${theme.palette.text.secondary} !important`,
              },
            })}
          >
            {translate('ra.action.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NamedDeleteButton;
